import type { RELATED_ACCOUNTS_SOURCES } from './components/ApplicantRelatedAccounts/constants';

type Nullable<T> = T | null;

// Values double as the `tab` URL query-param, so keep them short and readable.
export enum ApplicantTabId {
    Application = 'application',
    Ddd = 'ddd',
    Activity = 'activity',
    Documents = 'documents',
    Conditions = 'conditions',
    Tags = 'applicantTags',
    RelatedAccounts = 'applicantRelatedAccounts',
}

export const CONDITIONS_TAB_TEST_ID = 'tab-conditions';

// Mirrors the backend ApplicantPersona enum. Serialized as an integer on the
// applicant detail endpoint (Carrier is the backfilled default), so this must
// stay a numeric enum to match the wire format.
export enum ApplicantPersona {
    Unknown = 0,
    Carrier = 1,
    Driver = 2,
}

export enum BusinessTypes {
    LLC = 'LLC',
    SoleProprietorship = 'Sole Proprietorship',
    GeneralPartnership = 'General Partnership',
    LimitedPartnership = 'Limited Partnership',
    LimitedCompany = 'Limited Company',
    CCorporation = 'C Corporation',
    SCorporation = 'S Corporation',
}

// The applicant endpoint serializes this from the backend Business entity, which carries no
// string-enum converter on the column, so the value arrives as the raw enum number.
export enum TaxClassification {
    Unknown = 0,
    Individual = 1,
    SCorporation = 2,
    Partnership = 3,
    CCorporation = 4,
}

export const TaxClassificationLabels: Record<TaxClassification, string> = {
    [TaxClassification.Unknown]: '',
    [TaxClassification.Individual]: 'Individual',
    [TaxClassification.SCorporation]: 'S Corporation',
    [TaxClassification.Partnership]: 'Partnership',
    [TaxClassification.CCorporation]: 'C Corporation',
};

export interface FileProps {
    id: string;
    externalFileId: string;
    type: string;
    filePath: string;
    presignedUrl: string;
    originalFilename: string;
    filename: string;
    uploadDate: string;
    uploadedBy: Nullable<string>;
    isExternal: boolean;
}

export interface AddressProps {
    line1: string;
    line2: Nullable<string>;
    city: string;
    stateOrTerritory: string;
    zip: string;
}

export enum DecisionDetailStatusCode {
    Unknown = 0,
    Started = 1,
    Finished = 2,
    Created = 3,
    Abandoned = 4,
    Submitted = 5,
    UnderReview_Manual = 6,
    Approved = 7,
    ContractSent = 8,
    ContractSigned = 9,
    Onboarded = 10,
    Denied = 11,
    NeedMoreInfo = 12,
    Errored = 13,
    DeltaClientCreated = 14,
    InvoiceFactored = 15,
    Landed = 16,
    Invited = 17,
    UnderReview_Auto = 18,
}

export enum DecisionDetailStatuses {
    INVITED = 'Invited',
    LANDED = 'Landed',
    STARTED = 'Started',
    SUBMITTED = 'Submitted',
    UNDERREVIEW_MANUAL = 'UnderReview_Manual',
    UNDERREVIEW_AUTO = 'UnderReview_Auto',
    NEEDMOREINFO = 'NeedMoreInfo',
    ERRORED = 'Errored',
    DENIED = 'Denied',
    ABANDONED = 'Abandoned',
    APPROVED = 'Approved',
    CONTRACTSENT = 'ContractSent',
    // CONTACTSIGNED = 'ContactSigned',
    // ONBOARD = 'Onboard',
    CREATED = 'Created',
    FINISHED = 'Finished',
}

export interface DecisionDetailProps {
    id: string;
    status: DecisionDetailStatuses;
    details: Nullable<string>;
    dateCreated: string;
    dateModified: string;
    reviewerEmail: Nullable<string>;
    // Set on Submitted rows only: the recorded submission this status was decided on.
    applicantSubmissionId?: Nullable<string>;
}

export interface UnderWritingEvaluationProps {
    id: string;
    isSystemStalled: boolean;
    decisionDetailTableRecords: DecisionDetailProps[];
}

export interface BusinessInfoProps {
    id: string;
    createdBy: string;
    modifiedBy: string;
    dateCreated: string;
    dateModified: string;
    federalTaxId: string;
    legalName: string;
    dbaName: string;
    address: AddressProps;
    typeOfBusiness: keyof typeof BusinessTypes;
    taxClassification: Nullable<TaxClassification | keyof typeof TaxClassification>;
    stateOfOrganization: string;
    faaSPartnerId: string;
    corpPresidentLegalName: Nullable<string>;
    files?: FileProps[];
}

export enum LoadTypeKeys {
    Unknown = 0,
    Other,
    GeneralFreight,
    OilField,
    Hazmat,
    IntermodalContainer,
    MotorVehicle,
    Livestock,
    LessThanTruckload,
}

export const LoadTypes = {
    [LoadTypeKeys.Unknown]: 'Unknown',
    [LoadTypeKeys.MotorVehicle]: 'Motor Vehicle',
    [LoadTypeKeys.Livestock]: 'Livestock',
    [LoadTypeKeys.OilField]: 'Oil Field',
    [LoadTypeKeys.GeneralFreight]: 'General Freight',
    [LoadTypeKeys.Hazmat]: 'Hazmat',
    [LoadTypeKeys.IntermodalContainer]: 'Intermodal Container',
    [LoadTypeKeys.Other]: 'Other',
    [LoadTypeKeys.LessThanTruckload]: 'Less Than Truckload',
};

export interface CarrierInfoProps {
    id: string;
    partnerCarrierId: string;
    usDOT: string;
    stateDOT: string;
    stateDOTIssuer: string;
    mcNumber: string;
    companyOwnedTrucks: string;
    ownerOperators: string;
    createdBy: string;
    modifiedBy: string;
    dateCreated: string;
    dateModified: string;
    loadTypes: LoadTypeKeys[];
    haulsLoadsAcrossUsBorders: Nullable<boolean>;
}

export interface ContactInfoProps {
    id: string;
    createdBy: string;
    modifiedBy: string;
    dateCreated: string;
    dateModified: string;
    mailingAddress: AddressProps;
    physicalAddress?: AddressProps;
    businessEmail: string;
    businessPhone: string;
    businessPhoneExtension: string;
}

export interface OwnerProps {
    id: string;
    files: FileProps[];
    createdBy: string;
    modifiedBy: string;
    dateCreated: string;
    dateModified: string;
    legalFirstName: string;
    legalMiddleName: string;
    legalLastName: string;
    email: string;
    doB: string;
    ssnOrItin: string;
    mobilePhone: string;
    homePhone: string;
    homeAddress?: AddressProps;
    businessRole: string;
    businessOwnershipPercentage: string;
    isControllingManager: Nullable<boolean>;
    driversLicenseExpiration: Nullable<string>;
    driversLicenseNumber: Nullable<string>;
    driversLicenseState: Nullable<string>;
}

export interface LastPageVisited {
    applicantId: string;
    dateTime: string;
    id: string;
    path: string;
    rd: Nullable<string>;
    utmCampaign: Nullable<string>;
    utmContent: Nullable<string>;
    utmMedium: Nullable<string>;
    utmSource: string;
    utmTerm: Nullable<string>;
}

export interface ApplicantNote {
    applicantId: string;
    createdBy: string;
    dateCreated: string;
    id: string;
    note: string;
}

export interface DecisionDetailLink {
    id: string;
    factoringProductId: string;
    decisionDetailId: string;
    dateCreated: string;
    decisionDetail: DecisionDetailProps;
}

export interface FactoringProduct {
    id: string;
    leadId: string;
    opportunityId: string;
    accountId: string;
    applicantId: string;
    isSystemStalled: boolean;
    dateCreated: string;
    dateModified: string;
    decisionDetailLinks?: DecisionDetailLink[];
    mostRecentDecisionDetail?: DecisionDetailProps;
}

export interface LoadPayProduct {
    id: string;
    applicantId: string;
    dateCreated: string;
    dateModified: string;
    decisionDetailLinks?: DecisionDetailLink[];
    mostRecentDecisionDetail?: DecisionDetailProps;
    isSystemStalled: boolean;
}

export interface FuelProduct {
    id: string;
    applicantId: string;
    dateCreated: string;
    dateModified: string;
    decisionDetailLinks?: DecisionDetailLink[];
    mostRecentDecisionDetail?: DecisionDetailProps;
    isSystemStalled: boolean;
    nameOnCard: Nullable<string>;
    fuelCardsNeeded: Nullable<number>;
}

export interface LegalCheckbox {
    id: string;
    applicantId: string;
    isChecked: boolean | null;
    nameOfCheckbox: string;
    dateModified: string;
}

export interface InformationRequest {
    id: string;
    applicantId: string;
    isInternal: boolean;
    isPendingApplicantResponse: boolean;
    isActive: boolean;
    moreInfoType: number | null;
    notificationId: string | null;
    conditionId: string | null;
    moreInfoMessage: string;
    product: string;
    dateCreated: string;
    dateModified: string;
}

export interface Applicant {
    applicantNotes: ApplicantNote[];
    id: string;
    externalId: string;
    factoringProduct: FactoringProduct;
    faasPartnerId: string;
    partnerCarrierId: string;
    businessInfo: BusinessInfoProps;
    carrierInfo: CarrierInfoProps;
    contactInfo: ContactInfoProps;
    country: Nullable<string>;
    email: string;
    files: FileProps[];
    informationRequests: InformationRequest[];
    lastPageVisiteds: LastPageVisited[];
    legalCheckboxChecked: boolean;
    legalCheckboxCheckedDate: string;
    legalCheckboxes?: LegalCheckbox[];
    loadPayProduct: Nullable<LoadPayProduct>;
    tcsFuelCardProduct?: Nullable<FuelProduct>;
    owners: OwnerProps[];
    persona?: ApplicantPersona;
    phone: string;
    referredBy: Nullable<string>;
    salesforceAccountLink: Nullable<string>;
    salesforceLeadLink: Nullable<string>;
    salesforceOpportunityLink: Nullable<string>;
    isBusinessAPerson: Nullable<boolean>;
}

export enum DueDiligenceVendors {
    FMCSA = 'FMCSA',
}

export interface ApplicantDueDiligence {
    content: string | DueDiligenceCarrier;
    dueDiligenceRequestid: string;
    formattedContent: Nullable<string>;
    vendor: DueDiligenceVendors;
    vendorType: number;
}

export enum DueDiligence {
    Business = 'KY-Business',
    Carrier = 'KY-Carrier',
    People = 'KY-People',
}

export interface ApplicantDueDiligenceData {
    title: string;
    data: DueDiligenceDataPoint[];
    fileId?: Nullable<string>;
    // Absent on any block no status row can link to, which is how a dead "View DDD" link is avoided.
    vendor?: Nullable<string>;
    ownerId?: Nullable<string>;
    // Only the Middesk block sends these; its report is too long for one flat table.
    sections?: Nullable<DueDiligenceSection[]>;
    pulledAt?: Nullable<string>;
}
export interface DueDiligenceSection {
    title: string;
    count?: Nullable<number>;
    groups: DueDiligenceGroup[];
}
export interface DueDiligenceGroup {
    heading?: Nullable<string>;
    data: DueDiligenceDataPoint[];
}
export interface DueDiligenceDataPoint {
    label: string;
    value: string;
}

export interface DueDiligenceCarrier {
    allowedToOperate: string;
    bipdInsuranceOnFile: string;
    bipdInsuranceRequired: string;
    bipdRequiredAmount: string;
    bondInsuranceOnFile: string;
    bondInsuranceRequired: string;
    brokerAuthorityStatus: string;
    carrierOperation: {
        carrierOperationCode: string;
        carrierOperationDesc: string;
    };
    commonAuthorityStatus: string;
    contractAuthorityStatus: string;
    dbaName: Nullable<string>;
    dotNumber: number;
    ein: number;
    legalName: string;
    mcs150Outdated: string;
    oosDate: Nullable<string>;
    phyCity: string;
    phyCountry: string;
    phyState: string;
    phyStreet: string;
    phyZipcode: string;
    totalDrivers: number;
    totalPowerUnits: number;
}

export interface FMCSAContent {
    FMCSA_McNumber: {
        content: ParsedDueDilligenceCarrier[];
    };
    FMCSA_UsDot: {
        content: ParsedDueDilligenceCarrier;
    };
}

export interface ParsedDueDilligenceCarrier {
    carrier: DueDiligenceCarrier;
}

export interface ApplicantInfoProps {
    id: string;
    email: string;
    phone: string;
    partnerId: string;
    referredBy: Nullable<string>;
    lastPageVisited: string;
}

export enum VendorRequestTypes {
    // FMCSA
    McNumber = 1,
    UsDot = 2,
    Basics = 5,
    CargoCarried = 6,
    OperationClassification = 7,
    Oos = 8,
    DocketNumbers = 9,
    Authority = 10,
    // Alloy
    MiddeskOnly = 3,
    Validation = 4,
    // Triumph Carrier Service
    TriumpCarrierService = 11,
}

export const VendorRequestTypeTexts: { [key: number]: string } = {
    [VendorRequestTypes.McNumber]: 'McNumber',
    [VendorRequestTypes.UsDot]: 'UsDot',
    [VendorRequestTypes.Basics]: 'Basics',
    [VendorRequestTypes.CargoCarried]: 'CargoCarried',
    [VendorRequestTypes.OperationClassification]: 'OperationClassification',
    [VendorRequestTypes.Oos]: 'Oos',
    [VendorRequestTypes.DocketNumbers]: 'DocketNumbers',
    [VendorRequestTypes.Authority]: 'Authority',
    [VendorRequestTypes.MiddeskOnly]: 'MiddeskOnly',
    [VendorRequestTypes.Validation]: 'Validation',
    [VendorRequestTypes.TriumpCarrierService]: 'TriumpCarrierService',
};

export interface Document {
    association: string;
    documentType: string;
    fileId: string;
    fileName: string;
    uploadDateUtc: string;
    uploadedBy: Nullable<string>;
    linkedConditionId: Nullable<string>;
    isDeleted: boolean;
}

export { FileType } from './FileType.types';

export enum ApplicantNoteType {
    InternalNote = 'InternalNote',
    OriginationsAutomation = 'OriginationsAutomation',
}

export interface ActivityNote {
    id: string;
    note: string;
    email: string;
    timestampAdded: string;
    applicantNoteType: ApplicantNoteType;
}

// Mirrors the backend SourceSystem enum. Unlike other enums in this file,
// the backend serializes this as the string label (e.g., "ORP", "Applicant")
// rather than a numeric ID, so we model it as a string union type.
export type SourceSystem = 'Unknown' | 'ORP' | 'Salesforce' | 'Alloy' | 'Applicant' | 'System';

export interface ActivityProductStatus {
    product: string;
    status: string;
    timestampAdded: string;
    details: string | undefined;
    email: string | undefined;
    sourceSystem: SourceSystem | undefined;
    // Set only on a Submitted status that its own submit call linked to the recorded submission.
    applicantSubmissionId?: string | null;
}

export interface ActivityPageVisit {
    applicantReferrer: string;
    domain: string;
    pageVisited: string;
    rawUrl: string;
    timestampAdded: string;
    deviceType?: string;
    deviceBrowser?: string;
    deviceOs?: string;
    ipAddress?: string;
}

export interface ApplicantEmail {
    notificationId: string;
    subject: string;
    from: string | null;
    to: string[];
    cc: string[];
    bcc: string[];
    sourceSystem: string | null;
    timestampAdded: string;
    isSecureCode: boolean;
}

export interface ActivityInformationRequest {
    id: string;
    moreInfoType: 'DocsOnly' | 'FullApp' | null;
    author: string | null;
    message: string;
    product: string;
    timestampAdded: string;
    isReply: boolean;
}

export interface ApplicantActivityResponse {
    notes: ActivityNote[];
    productStatuses: ActivityProductStatus[];
    pageVisits: ActivityPageVisit[];
    emails: ApplicantEmail[];
    informationRequests: ActivityInformationRequest[];
}

export interface BusinessRepresentative {
    isPrimaryAccountHolder?: boolean;
    firstName?: string;
    lastName?: string;
    email?: string;
}

export enum AutomaticDeltaFundingMethodLinkState {
    Linked = 'Linked',
    InProgressBankInfoReadyAndDeltaIdReady = 'InProgressBankInfoReadyAndDeltaIdReady',
    InProgressBankInfoReadyAndDeltaIdPending = 'InProgressBankInfoReadyAndDeltaIdPending',
    InProgressBankInfoPendingAndDeltaIdReady = 'InProgressBankInfoPendingAndDeltaIdReady',
    InProgressBankInfoPendingAndDeltaIdPending = 'InProgressBankInfoPendingAndDeltaIdPending',
    SuspectedRelationshipDisabled = 'SuspectedRelationshipDisabled',
}

export interface LoadPayRelatedAccount {
    companyApplicationId?: string;
    companyApplicationStatus?: string;
    companyApplicationCreatedAt?: string;
    companyApplicationSubmittedAt?: string;
    companyId?: string;
    companyCreatedAt?: string;
    companyAccountStatus?: string;
    externalApplicantId?: string;
    businessName?: string;
    businessRepresentatives?: BusinessRepresentative[];
    isDirectlyLinked: boolean;
    hasApplicantData: boolean;
    automaticDeltaFundingMethodLinkState?: AutomaticDeltaFundingMethodLinkState;
    deltaClientId?: number | null;
}

export interface RelatedOriginationsApplicant {
    applicantId: string;
    partnerGroup: string;
    companyName: string;
    matchedOn: string[];
    status: string[];
    dateCreated: string;
}

export interface RelatedDeltaClient {
    deltaClientId: number;
    deltaClientLink: Nullable<string>;
    carrierName: Nullable<string>;
    isInactive: boolean;
    lastFundingDate: Nullable<string>;
    usDot: Nullable<string>;
    mc: Nullable<string>;
    email: Nullable<string>;
    phone: Nullable<string>;
    matchedOn: string[];
    isDirectlyLinked: boolean;
    originationsApplicantId: Nullable<string>;
}

export type RelatedAccountsSource =
    (typeof RELATED_ACCOUNTS_SOURCES)[keyof typeof RELATED_ACCOUNTS_SOURCES];

export interface RelatedAccountsResponse {
    loadPayAccounts: LoadPayRelatedAccount[];
    originationsApplicants: RelatedOriginationsApplicant[];
    deltaClients?: RelatedDeltaClient[];
    /** Sources whose lookup failed; their list is empty because it is unknown, not because nothing matched. */
    unavailableSources?: RelatedAccountsSource[];
}

export interface PointOfContact {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    products: string[];
    stage: string;
    shouldCcOnEmail: boolean;
}
